import { FC, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { BugoStatRangeQueryEnum } from '@service/bugo/page/AgencyAdminBugoStatMobilePage';
import { FeventListItem, FeventListSummary } from '@shared/api/fevent/fevent.interface';
import CheckboxFilter, { CheckBoxFilter } from '@shared/components/CheckboxFilter';
import { DatePicker } from '@shared/containers/DatePicker/DatePicker';
import { customThemeAtom } from '@shared/state/atom/theme.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { getDefaultDateFrom, getTodayFrom, getTodayTo } from '@shared/utils/dateUtils';
import { formatDate } from '@shared/utils/formatDate';
import { getRoughRegion } from '@shared/utils/getRoughRegion';
import { mainBgColorGenerator } from '@shared/utils/mainColorGenerator';
import { Table, TableColumnsType } from 'antd';
import FuzzySearch from 'fuzzy-search';
import _ from 'lodash';

import BugoDetailModalOpener from '../BugoDetailModalOpener/BugoDetailModalOpener';
import WorkerDetailModalOpener from '../WorkerDetailModalOpener/WorkerDetailModalOpener';
import { AgencyAdminBugoStatusBar } from './AgencyAdminBugoStatusBar';

interface IProps {
  feventList: FeventListItem[];
  summary?: FeventListSummary;
  teamTypesFilter: CheckBoxFilter | null;
  regionsFilter: CheckBoxFilter;
  range: BugoStatRangeQueryEnum | null;
  searchValue: string;
}

type FeventTableData = {
  id: string;
  index: number;
  workerName: string;
  workerId: string;
  teamName: string;
  funeralHomeName: string;
  region: string;
  createdAt: Date | '';
  orderListLen: number;
};

const AgencyAdminBugoListTableMobile: FC<IProps> = ({
  feventList,
  summary,
  teamTypesFilter,
  regionsFilter,
  range,
  searchValue,
}: IProps) => {
  const customTheme = useRecoilValue(customThemeAtom);
  const { isBugoAgencyBranchAdmin } = useAuth();

  const [dateFrom, setDateFrom] = useState<Date | null>(
    range ? getDefaultDateFrom(range) : null,
  );
  const [dateTo, setDateTo] = useState<Date | null>(range ? new Date() : null);

  const [dateFilteredList, setDateFilteredList] = useState<FeventListItem[]>(
    feventList ?? [],
  );

  const [selectedTeamTypes, setSelectedTeamTypes] = useState(teamTypesFilter);

  const teamTypesFilteredList = useMemo(() => {
    if (selectedTeamTypes)
      return dateFilteredList.filter((item) => selectedTeamTypes[item.workerTeamType ?? '']);
    else return dateFilteredList;
  }, [dateFilteredList, selectedTeamTypes]);

  const [selectedRegions, setSelectedRegions] = useState(regionsFilter);

  const regionsFilteredList = useMemo(() => {
    return teamTypesFilteredList.filter(
      (item) =>
        selectedRegions[getRoughRegion(item.funeralHomeAddress?.split(' ')[0] ?? '')],
    );
  }, [teamTypesFilteredList, selectedRegions]);

  const resultValue = useMemo(() => {
    const searcher = new FuzzySearch(regionsFilteredList, [
      'workerName',
      'funeralHomeName',
      'funeralHomeAddress',
      'registerNumber',
      'workerTeamType',
      'workerTeamName',
    ]);
    return searcher.search(searchValue);
  }, [regionsFilteredList, searchValue]);

  const tableData: FeventTableData[] = useMemo(() => {
    return _.map(resultValue, (item: FeventListItem, index: number) => {
      return {
        id: item._id,
        index: index,
        workerName: item.workerName,
        workerId: item.workerId,
        teamName: item.workerTeamName,
        region: item.workerRegion,
        funeralHomeName: item.funeralHomeName,
        createdAt: item.createdAt,
        orderListLen: item.shopOrders?.length ?? 0,
      };
    });
  }, [resultValue]);

  useEffect(() => {
    if (!feventList) return;

    const rangeFilteredList = feventList.filter((item) => {
      if (!item.createdAt) return false;
      const createdAt = new Date(item.createdAt).getTime();

      if (dateFrom && dateTo) {
        return dateFrom.getTime() <= createdAt && createdAt <= dateTo.getTime();
      }

      if (!dateFrom && !dateTo) return true;

      return false;
    });

    setDateFilteredList(rangeFilteredList);
  }, [dateFrom, dateTo, feventList]);

  const columns: TableColumnsType<FeventTableData> = isBugoAgencyBranchAdmin()
    ? [
        {
          title: <div className="break-keep">등록일</div>,
          dataIndex: 'createdAt',
          key: 'createdAt',
          align: 'center',
          render: (value: Date) => {
            return (
              <p className="text-xs text-gray-600">
                {formatDate(value, {
                  contains: { year: false, date: true },
                  dateSeparater: '/',
                })}
              </p>
            );
          },
        },
        {
          width: '15%',
          title: <div className="break-keep">소속</div>,
          dataIndex: 'teamName',
          key: 'teamType',
          align: 'center',
          render: (teamName: string) => {
            return <p className="break-keep text-xs">{teamName}</p>;
          },
        },
        {
          width: '15%',
          title: <div className="break-keep">지도사</div>,
          key: 'worker',
          align: 'center',
          render: (value: FeventTableData) => {
            return (
              <WorkerDetailModalOpener
                workerName={value.workerName}
                workerId={value.workerId}
              />
            );
          },
        },
        {
          width: '35%',
          title: <div className="break-keep">장례식장</div>,
          dataIndex: 'funeralHomeName',
          key: 'funeralHomeName',
          align: 'center',
          render: (funeralHomeName) => {
            return <p className="text-xs">{funeralHomeName.split('장례')[0]}</p>;
          },
        },
        {
          width: '15%',
          title: <div className="break-keep">화환 판매</div>,
          key: 'sentBugosLen/orderListLen',
          align: 'center',
          render: (value: FeventTableData) => {
            const { orderListLen } = value;
            return (
              <p className="text-xs text-gray-600">{`${orderListLen.toLocaleString()}`}</p>
            );
          },
        },
      ]
    : [
        {
          title: <div className="break-keep">등록일</div>,
          dataIndex: 'createdAt',
          key: 'createdAt',
          align: 'center',
          render: (value: Date) => {
            return (
              <p className="text-xs text-gray-600">
                {formatDate(value, {
                  contains: { year: false, date: true },
                  dateSeparater: '/',
                })}
              </p>
            );
          },
        },
        {
          width: '15%',
          title: <div className="break-keep">소속</div>,
          dataIndex: 'teamName',
          key: 'teamType',
          align: 'center',
          render: (teamName: string) => {
            return <p className="break-keep text-xs">{teamName}</p>;
          },
        },
        {
          width: '15%',
          title: <div className="break-keep">지도사</div>,
          key: 'worker',
          align: 'center',
          render: (value: FeventTableData) => {
            return (
              <WorkerDetailModalOpener
                workerName={value.workerName}
                workerId={value.workerId}
              />
            );
          },
        },
        {
          width: '35%',
          title: <div className="break-keep">장례식장</div>,
          dataIndex: 'funeralHomeName',
          key: 'funeralHomeName',
          align: 'center',
          render: (funeralHomeName) => {
            return <p className="text-xs">{funeralHomeName.split('장례')[0]}</p>;
          },
        },
        {
          width: '15%',
          title: <div className="break-keep">화환 판매</div>,
          key: 'sentBugosLen/orderListLen',
          align: 'center',
          render: (value: FeventTableData) => {
            const { orderListLen } = value;
            return (
              <p className="text-xs text-gray-600">{`${orderListLen.toLocaleString()}`}</p>
            );
          },
        },
        {
          width: '15%',
          title: '상세',
          key: 'bugo modal button',
          align: 'center',
          render: (value: FeventTableData) => {
            return (
              <div className="center-box">
                <BugoDetailModalOpener feventId={value.id} />
              </div>
            );
          },
        },
      ];

  const onDateFromChange = (date: Date | null) => {
    // 설정된 시작일이 종료일보다 클 경우 switch.
    if (dateTo && date && dateTo.getTime() < date.getTime()) {
      setDateFrom(getTodayFrom(dateTo));
      setDateTo(getTodayTo(date));
      return;
    }

    setDateFrom(date ? getTodayFrom(date) : null);
  };

  const onDateFromTo = (date: Date | null) => {
    // 설정된 종료일이 시작일보다 작을 경우 switch.
    if (dateFrom && date && dateFrom.getTime() > date.getTime()) {
      setDateTo(getTodayTo(dateFrom));
      setDateFrom(getTodayFrom(date));
      return;
    }

    setDateTo(date ? getTodayTo(date) : null);
  };

  return (
    <div className="space-y-2 px-2">
      {/* Date Range Picker */}
      <div className="flex items-center space-x-2 bg-opacity-60">
        {/* 시작일 */}
        <DatePicker
          placeholder="시작일"
          value={dateFrom}
          onChange={onDateFromChange}
          className="flex-1"
        />
        <div>~</div>
        {/* 종료일 */}
        <DatePicker
          placeholder="종료일"
          value={dateTo}
          onChange={onDateFromTo}
          className="flex-1"
        />
      </div>
      {/* 소속 필터 */}
      {selectedTeamTypes && (
        <div className="flex items-center border text-xs theme-bg-1 theme-border-1">
          <div
            className={`center-box self-stretch bg-opacity-40 px-2 font-bold ${mainBgColorGenerator(
              customTheme,
            )}`}
          >
            소속
          </div>
          <div className="flex flex-1 items-center justify-around">
            <CheckboxFilter
              filter={selectedTeamTypes}
              setFilter={(checked) => setSelectedTeamTypes(checked)}
            />
          </div>
        </div>
      )}
      {/* 지역 필터 */}
      <div className="flex items-center border text-xs theme-bg-1 theme-border-1">
        <div
          className={`center-box self-stretch bg-opacity-40 px-2 font-bold ${mainBgColorGenerator(
            customTheme,
          )}`}
        >
          지역
        </div>
        <div className="flex flex-1 items-center justify-around">
          <CheckboxFilter
            filter={selectedRegions}
            setFilter={(checked) => setSelectedRegions(checked)}
          />
        </div>
      </div>
      <div className="text-xs">
        <AgencyAdminBugoStatusBar summary={summary} />
      </div>
      <Table
        className="w-full"
        columns={columns}
        dataSource={_.flatMapDeep([tableData])}
        pagination={{ position: ['bottomCenter'], pageSize: 15 }}
        rowKey={'id'}
        size={'small'}
        onHeaderRow={() => ({
          className: `text-xs font-bold bg-opacity-40 ${mainBgColorGenerator(
            customTheme,
          )}`,
        })}
      />
    </div>
  );
};

export default AgencyAdminBugoListTableMobile;
